import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from "react";
import { Button, Input } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import Header from "../../layout/Header/Header";
import { AuthContext } from "../../contexts/AuthContext";
import ModeloModal from "./ModeloModal";
import FotoModelo from "./FotoModelo";

// Catálogo de modelos para tiendas: datos de venta, colores y tallas, catálogos y carga por Excel
const API = process.env.REACT_APP_URL_POS ? process.env.REACT_APP_URL_POS.replace(/\/pos$/, "") : "";
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("app_token")}` } });
const msg = (e) => (e.response && e.response.data && e.response.data.message) || "Intenta de nuevo";
const dinero = (n) => "$" + Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const CATS = [["lineas", "Líneas"], ["marcas", "Marcas"], ["colecciones", "Colecciones"], ["familias", "Familias"], ["colores", "Colores"], ["tallas", "Tallas"]];
const SINGULAR = { lineas: "línea", marcas: "marca", colecciones: "colección", familias: "familia", colores: "color", tallas: "talla" };


function Modelos() {
  const { user } = useContext(AuthContext);
  const puedeEditar = !!(user && user.articulos_create);
  const verCostos = !!(user && user.posCostos);
  const [tab, setTab] = useState("modelos");
  const [modelos, setModelos] = useState([]);
  const [cat, setCat] = useState(Object.fromEntries(CATS.map(([k]) => [k, []])));
  const [texto, setTexto] = useState("");
  const [linea, setLinea] = useState("");
  const [activos, setActivos] = useState("Si");
  const [editando, setEditando] = useState(null); // null cerrado · {} nuevo · modelo
  const [carga, setCarga] = useState(null); // { filas, resumen }
  const archivo = useRef(null);

  const cargarCat = useCallback(() => axios.get(`${API}/modelos/catalogos`, auth()).then((r) => setCat(r.data)).catch(() => {}), []);
  const cargar = useCallback(() => {
    axios.get(`${API}/modelos`, { ...auth(), params: { activos, linea: linea || undefined } }).then((r) => setModelos(r.data)).catch((e) => Swal.fire({ icon: "error", title: "No se pudo cargar", text: msg(e) }));
  }, [activos, linea]);
  useEffect(() => { cargarCat(); }, [cargarCat]);
  useEffect(() => { cargar(); }, [cargar]);

  const vis = useMemo(() => {
    const t = texto.trim().toLowerCase();
    return t ? modelos.filter((m) => `${m.codigo} ${m.nombre} ${m.EAN || ""}`.toLowerCase().includes(t)) : modelos;
  }, [modelos, texto]);

  const abrir = (m) => setEditando(m || {});

  const nuevoEnCatalogo = async (tipo, alCrear) => {
    const r = await Swal.fire({ title: `Nueva ${SINGULAR[tipo]}`, input: "text", showCancelButton: true, confirmButtonText: "Agregar", cancelButtonText: "Cancelar", inputValidator: (v) => (!v || !v.trim() ? "Escribe el nombre" : undefined) });
    if (!r.value) return;
    try {
      const x = (await axios.post(`${API}/modelos/catalogo/${tipo}`, { name: r.value }, auth())).data;
      await cargarCat();
      if (alCrear) alCrear(x._id);
    } catch (e) { Swal.fire({ icon: "error", title: "No se agregó", text: msg(e) }); }
  };


  // ---------- Excel ----------
  const plantilla = () => {
    const ws = XLSX.utils.aoa_to_sheet([
      ["codigo", "nombre", "linea", "marca", "coleccion", "familia", "precio", "costo", "colores", "tallas", "codigo_barras"],
      ["NF-BL-101", "Blusa lino manga corta", "Blusas", "Nina Ferre", "Primavera-Verano 2026", "Dama", 449, 180, "Negro, Blanco", "CH, M, G, XG", ""],
      ["NF-AC-101", "Bolsa tote mediana", "Accesorios", "Nina Ferre", "Básicos", "Complementos", 399, 150, "", "", "7501234567890"],
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Modelos");
    XLSX.writeFile(wb, "plantilla_modelos.xlsx");
  };
  const leerExcel = (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    const lector = new FileReader();
    lector.onload = async (ev) => {
      try {
        const wb = XLSX.read(ev.target.result, { type: "array" });
        const crudas = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "" });
        const filas = crudas.map((x) => {
          const k = {};
          Object.keys(x).forEach((c) => { k[c.toString().trim().toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/\s+/g, "_")] = x[c]; });
          return { codigo: k.codigo, nombre: k.nombre, linea: k.linea, marca: k.marca, coleccion: k.coleccion, familia: k.familia, precio: k.precio, costo: k.costo, colores: k.colores, tallas: k.tallas, codigo_barras: k.codigo_barras || k.ean };
        });
        const resumen = (await axios.post(`${API}/modelos/importar`, { filas, soloValidar: true }, auth())).data;
        setCarga({ filas, resumen, nombre: f.name });
      } catch (er) { Swal.fire({ icon: "error", title: "No se pudo leer", text: msg(er) }); }
    };
    lector.readAsArrayBuffer(f);
  };
  const confirmarCarga = async () => {
    try {
      const r = (await axios.post(`${API}/modelos/importar`, { filas: carga.filas }, auth())).data;
      Swal.fire({ icon: "success", title: "Modelos cargados", text: `${r.nuevosModelos} nuevos, ${r.actualizados} actualizados` });
      setCarga(null); cargarCat(); cargar(); setTab("modelos");
    } catch (e) { Swal.fire({ icon: "error", title: "No se cargó", text: msg(e) }); }
  };

  if (!(user && (user.menu_articulos || user.articulos_create))) {
    return (<><Header /><div className="card container col-12 p-4"><div className="alert alert-warning mb-0">No tienes permiso para ver modelos.</div></div></>);
  }


  return (
    <>
      <Header />
      <div className="card container col-12" style={{ padding: "18px 22px" }}>
        <div className="page-header">
          <div>
            <h3 className="page-title"><i className="fas fa-tshirt mr-2" style={{ color: "#8b5cf6" }} />Modelos</h3>
            <p className="page-subtitle">Cada modelo se vende por color y talla; aquí se definen cuáles tiene</p>
          </div>
          <div className="page-actions">
            {puedeEditar && <Button color="success" onClick={() => abrir(null)}><i className="fas fa-plus mr-1" />Nuevo modelo</Button>}
          </div>
        </div>

        <div className="btn-group mb-3" role="group">
          {[["modelos", "Modelos", "fas fa-tshirt"], ["catalogos", "Catálogos", "fas fa-tags"], puedeEditar && ["excel", "Cargar Excel", "fas fa-file-excel"]].filter(Boolean).map(([k, l, ic]) => (
            <Button key={k} color={tab === k ? "primary" : "secondary"} outline={tab !== k} onClick={() => setTab(k)}><i className={`${ic} mr-1`} />{l}</Button>
          ))}
        </div>

        {tab === "modelos" && (
          <>
            <div className="filter-bar">
              <div style={{ flex: 1, minWidth: 220 }}><label>Buscar</label><Input value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Código, nombre o código de barras" /></div>
              <div style={{ minWidth: 180 }}><label>Línea</label>
                <Input type="select" value={linea} onChange={(e) => setLinea(e.target.value)}>
                  <option value="">Todas</option>{cat.lineas.map((l) => <option key={l._id} value={l._id}>{l.name}</option>)}
                </Input>
              </div>
              <div><label>Mostrar</label>
                <Input type="select" value={activos} onChange={(e) => setActivos(e.target.value)}>
                  <option value="Si">Activos</option><option value="No">Inactivos</option><option value="todos">Todos</option>
                </Input>
              </div>
              <div className="stat-chip" style={{ alignSelf: "center" }}>Modelos <span>{vis.length}</span></div>
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-dark-header">
                <thead><tr><th style={{ width: 52 }} /><th>Código</th><th>Modelo</th><th>Línea</th><th>Colores</th><th>Tallas</th><th className="text-right">Precio</th>{verCostos && <th className="text-right">Costo</th>}<th className="text-right">En tiendas</th><th /></tr></thead>
                <tbody>
                  {!vis.length && <tr><td colSpan={10} className="text-center text-muted py-4">Sin modelos</td></tr>}
                  {vis.slice(0, 1500).map((m) => (
                    <tr key={m._id} style={m.is_active === "No" ? { color: "#94a3b8" } : {}}>
                      <td><FotoModelo url={m.urlFoto} nombre={`${m.codigo} · ${m.nombre}`} size={40} /></td>
                      <td><strong>{m.codigo}</strong>{m.EAN ? <div className="small text-muted">{m.EAN}</div> : null}</td>
                      <td>{m.nombre}{m.marcas[0] ? <div className="small text-muted">{m.marcas[0].name}{m.colecciones[0] ? ` · ${m.colecciones[0].name}` : ""}</div> : null}</td>
                      <td>{m.lineas[0] ? m.lineas[0].name : "—"}</td>
                      <td style={{ fontSize: "0.82rem" }}>{m.colores.map((c) => c.name).join(", ") || "—"}</td>
                      <td style={{ fontSize: "0.82rem" }}>{m.tallas.map((t) => t.name).join(" · ") || "—"}</td>
                      <td className="text-right">{dinero(m.precioTienda)}</td>
                      {verCostos && <td className="text-right">{dinero(m.costoMX)}</td>}
                      <td className="text-right">{m.existencia}</td>
                      <td>{puedeEditar && <Button size="sm" color="info" onClick={() => abrir(m)}><i className="fas fa-pen" /></Button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {vis.length > 1500 && <div className="text-muted">Mostrando 1,500 de {vis.length}; usa el buscador.</div>}
            </div>
          </>
        )}

        {tab === "catalogos" && (
          <div className="row">
            {CATS.map(([k, titulo]) => (
              <div key={k} className="col-md-6 col-xl-4 mb-3">
                <div className="form-section" style={{ height: "100%" }}>
                  <div className="form-section-header"><h4 className="form-section-title">{titulo}</h4>
                    {puedeEditar && <Button size="sm" color="link" className="ml-auto p-0" onClick={() => nuevoEnCatalogo(k)}>+ Agregar</Button>}
                  </div>
                  <div className="form-section-body" style={{ maxHeight: 260, overflowY: "auto", padding: "8px 16px" }}>
                    {!cat[k].length && <div className="text-muted small">Vacío</div>}
                    {cat[k].map((o) => (
                      <div key={o._id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "3px 0", borderBottom: "1px solid #f1f5f9", color: o.is_active === "No" ? "#94a3b8" : undefined }}>
                        <span>{o.name}{o.is_active === "No" ? " (inactivo)" : ""}</span>
                        {puedeEditar && (
                          <span style={{ whiteSpace: "nowrap" }}>
                            <Button size="sm" color="link" className="p-0 mr-2" title="Renombrar" onClick={async () => {
                              const r = await Swal.fire({ title: `Renombrar ${SINGULAR[k]}`, input: "text", inputValue: o.name, showCancelButton: true, confirmButtonText: "Guardar", cancelButtonText: "Cancelar" });
                              if (!r.value) return;
                              try { await axios.patch(`${API}/modelos/catalogo/${k}/${o._id}`, { name: r.value }, auth()); cargarCat(); cargar(); } catch (e) { Swal.fire({ icon: "error", title: "No se guardó", text: msg(e) }); }
                            }}><i className="fas fa-pen" /></Button>
                            <Button size="sm" color="link" className="p-0" title={o.is_active === "No" ? "Activar" : "Desactivar"} onClick={async () => {
                              try { await axios.patch(`${API}/modelos/catalogo/${k}/${o._id}`, { is_active: o.is_active === "No" ? "Si" : "No" }, auth()); cargarCat(); } catch (e) { Swal.fire({ icon: "error", title: "No se guardó", text: msg(e) }); }
                            }}><i className={`fas fa-${o.is_active === "No" ? "eye" : "eye-slash"}`} /></Button>
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "excel" && puedeEditar && (
          <div className="form-section">
            <div className="form-section-body">
              <p className="mb-2">Columnas: <code>codigo, nombre, linea, marca, coleccion, familia, precio, costo, colores, tallas, codigo_barras</code>. Colores y tallas separados por coma. Si el código ya existe, el modelo se actualiza; las líneas, marcas, colores o tallas que no existan se crean.</p>
              <input ref={archivo} type="file" accept=".xlsx,.xls,.csv" style={{ display: "none" }} onChange={leerExcel} />
              <Button color="success" onClick={() => archivo.current.click()}><i className="fas fa-file-excel mr-1" />Elegir archivo</Button>{" "}
              <Button color="link" onClick={plantilla}>Descargar plantilla</Button>
              {carga && (
                <div className="mt-3">
                  <h5 style={{ fontSize: "0.95rem" }}>{carga.nombre}</h5>
                  <div className="stats-row">
                    <div className="stat-chip">Modelos <span>{carga.resumen.modelos}</span></div>
                    <div className="stat-chip">Nuevos <span>{carga.resumen.nuevosModelos}</span></div>
                    <div className="stat-chip">Se actualizan <span>{carga.resumen.actualizados}</span></div>
                  </div>
                  {Object.entries(carga.resumen.catalogosNuevos).filter(([, v]) => v.length).map(([k, v]) => (
                    <div key={k} className="small mb-1"><strong>Se crearán {k}:</strong> {v.join(", ")}</div>
                  ))}
                  {carga.resumen.errores.length > 0 ? (
                    <div className="alert alert-danger mt-2">
                      <strong>{carga.resumen.errores.length} problema(s); corrige el archivo y vuelve a cargarlo:</strong>
                      <ul className="mb-0">{carga.resumen.errores.slice(0, 30).map((x, i) => <li key={i}>Fila {x.fila} · {x.codigo}: {x.motivo}</li>)}</ul>
                    </div>
                  ) : (
                    <Button color="success" className="mt-2" onClick={confirmarCarga}><i className="fas fa-check mr-1" />Confirmar carga</Button>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <ModeloModal modelo={editando} onCerrar={() => setEditando(null)} onGuardado={() => { setEditando(null); cargar(); }} onCatalogo={cargarCat} />
    </>
  );
}

export default Modelos;
